import { Injectable } from '@angular/core';
import {
  AffordabilityRequest,
  BdgAffordabilityRequest,
  BtlAffordabilityRequest,
} from '@clubhub/ignite/models/affordability';
import { FeeCalculationScenario, ProductFeeType } from '@shared/constants';
import {
  BridgingProduct,
  LenderCapitalRestPeriod,
  Loan,
  LoanPeriod,
  LoanPeriodCalculation,
  LoanType,
  MethodOfRepayment,
  MonthlyCalculation,
  Product,
  ProductErcChargeBasis,
  ProductsBridgingModelRequest,
  ProductsBtlModelRequest,
  ProductsResidentialModelRequest,
  RateType,
  RepaymentMethod,
  ResiBtlProductErc,
} from '@shared/models';
import { CustomBridgingProductFees, CustomFee, CustomProductFees } from '@shared/models/custom-product-calculations';

interface CalculationResult {
  trueCost: number;
  trueCostInitial: number | null;
  trueCostCustom: number | null;
  initialMonthlyPayment: number;
  monthlyPaymentRevert: number;
  isForTerm: boolean;
  outstandingBalance: number;
  ercCharge?: number;
}

interface FullCalculationResult extends CalculationResult {
  loanPeriodCalculations: LoanPeriodCalculation[];
  aprc: number;
  totalInterestPayable: number;
}

class MonthlyPaymentCalculations {
  public constructor(
    public monthlyPayments: number,
    public principalRepaid: number,
    public monthlyPaymentsIo: number,
    public principalRepaidIo: number,
    public term: number,
    public isRevert: boolean,
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class ProductsCalculationService {
  private readonly maxAprcIterations = 16;

  /**
   * The shortest default True Cost period, in months, for a product with an initial period (MAV-702). Rounding to the
   * nearest year takes an initial period of under 10 months to 0 months, which left a default True Cost of just the
   * one-off fees less cashback. Matches MinimumDefaultTrueCostPeriod in the backend's StandardCalcsService.
   */
  private readonly minimumDefaultTrueCostPeriod = 12;

  public performLoanCalculation(
    loan: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): FullCalculationResult {
    switch (loan.capitalRestPeriod) {
      case LenderCapitalRestPeriod.Daily:
      case LenderCapitalRestPeriod.Monthly:
        return this.performLoanCalculationMonthly(loan, product, fees, customTrueCostPeriod, referenceDate);
      case LenderCapitalRestPeriod.Annual:
        return this.performLoanCalculationYearly(loan, product, fees, customTrueCostPeriod, referenceDate);
      default:
        throw new Error('Unsupported capital rest period.');
    }
  }

  private performLoanCalculationMonthly(
    loan: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): FullCalculationResult {
    const { loanAmount, loanAmountIo } = this.getLoanAmountsIncludingFees(loan);
    const { adjustedModel, rolledUpLoanAmount, rolledUpLoanAmountIo } =
      this.adjustLoanModel(loan, loanAmount, loanAmountIo);

    const loanPeriodCalculations = this.performLoanPeriodCalculations(adjustedModel);
    const monthlyPaymentCalculations = this.performMonthlyCalculations(adjustedModel, loanPeriodCalculations);

    const aprc: number = this.calculateAPRCMonthly(adjustedModel);

    const trueCost: number = this.calculateTrueCost(monthlyPaymentCalculations, adjustedModel);

    const isForTerm = adjustedModel.loanPeriods[0].term < 1;

    const trueCostInitial: number | null = isForTerm ? null :
      this.calculateTrueCostInitial(adjustedModel, loanPeriodCalculations, null);

    const trueCostCustomBase = this.calculateCustomTrueCost(
      rolledUpLoanAmount, rolledUpLoanAmountIo, trueCost, loan, customTrueCostPeriod);
    const ercCharge = this.calculateErcCharge(
      product,
      adjustedModel,
      rolledUpLoanAmount,
      rolledUpLoanAmountIo,
      fees,
      customTrueCostPeriod ?? 0,
      loanPeriodCalculations,
      monthlyPaymentCalculations,
      referenceDate,
    );
    const trueCostCustom = trueCostCustomBase + ercCharge;

    const totalInterestPayable: number = this.calculateTotalInterestPayable(monthlyPaymentCalculations);
    return this.toResult({
      loanPeriodCalculations,
      trueCost,
      trueCostInitial,
      trueCostCustom,
      aprc,
      totalInterestPayable,
      isForTerm,
      outstandingBalance: 0,
      ercCharge,
    });
  }

  private performLoanCalculationYearly(
    model: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): FullCalculationResult {
    // A for-term product has a single rate (a revert-style period) for the whole term. Stepped products, with more
    // than one initial period, are not supported.
    const isForTerm = model.loanPeriods.length === 1 && model.loanPeriods[0].term === -1;
    if (!isForTerm && !(model.loanPeriods.length === 2 && model.loanPeriods[1].term === -1)) {
      throw new Error(
        'Yearly calculations only support for-term products or products with one initial period plus a revert period.');
    }

    // Opening balances, built as the monthly calculation builds them: fees added to the loan sit on the
    // repayment part, or on the interest-only part for an interest-only loan. A delayed start rolls both parts up at
    // the first period's rates and takes the delay off the first period and the term, with no payments during the
    // delay, as on the monthly path.
    const { loanAmount, loanAmountIo } = this.getLoanAmountsIncludingFees(model);
    const { adjustedModel, rolledUpLoanAmount: repaymentBalance, rolledUpLoanAmountIo: interestOnlyBalance } =
      this.adjustLoanModel(model, loanAmount, loanAmountIo);
    const delayedStart = Math.max(model.delayedStart ?? 0, 0);
    const term = adjustedModel.term;

    // Initial capital repayment. A for-term product has no initial period: its single rate is charged for the whole
    // term, so this is the annuity over the full term.
    const initialRate = model.loanPeriods[0].repaymentRate;
    const initialPeriod = isForTerm ? 0 : Math.max(adjustedModel.loanPeriods[0].term, 0);
    const annualPayment =
      initialRate * repaymentBalance /
      (1 - Math.pow(1 + initialRate, -term / 12));
    const repaymentInitialRepayment = annualPayment / 12;

    // Initial interest only
    const initialRateMonthly = initialRate / 12;
    const interestOnlyInitialMonthlyPayment = initialRateMonthly * interestOnlyBalance;

    // Revert capital repayment. For a for-term product this is the same single rate and full-term payment.
    // MCONE-5628 - If the revert rate is lower than the initial rate, we use the initial rate instead
    const revertRate = Math.max(model.loanPeriods[model.loanPeriods.length - 1].repaymentRate, initialRate);
    const principalRepaidInitialPeriod = (repaymentInitialRepayment * 12 - initialRate * repaymentBalance) *
      (Math.pow(1 + initialRate, initialPeriod / 12) - 1) / initialRate;
    const repaymentBalanceAtRevert = repaymentBalance - principalRepaidInitialPeriod;

    const monthlyPaymentRevert = isForTerm
      ? repaymentInitialRepayment
      : revertRate * repaymentBalanceAtRevert /
        12 / (1 - Math.pow(1 + revertRate, -((term - initialPeriod) / 12)));

    // Revert interest only
    const revertRateMonthly = revertRate / 12;
    const interestOnlyRevertMonthlyPayment = revertRateMonthly * interestOnlyBalance;

    // The repayment part's balance after the given number of monthly payments. The capital is repaid annually, so the
    // balance follows the annual-rest annuity, P - (A - rP)((1 + r)^(months / 12) - 1) / r: at the initial rate and
    // payment during the initial period, then from the balance at the end of it at the revert rate and payment.
    const repaymentBalanceAfter = (months: number) => months < initialPeriod
      ? repaymentBalance - (annualPayment - initialRate * repaymentBalance) *
        (Math.pow(1 + initialRate, months / 12) - 1) / initialRate
      : repaymentBalanceAtRevert - (monthlyPaymentRevert * 12 - revertRate * repaymentBalanceAtRevert) *
        (Math.pow(1 + revertRate, (months - initialPeriod) / 12) - 1) / revertRate;

    // True cost: the repayment part's payments, plus the interest-only part's interest and its capital, which is
    // repaid at the end of the term
    const trueCost = repaymentInitialRepayment * initialPeriod + monthlyPaymentRevert * (term - initialPeriod)
      + interestOnlyInitialMonthlyPayment * initialPeriod
      + interestOnlyRevertMonthlyPayment * (term - initialPeriod)
      + interestOnlyBalance
      + model.additionalFees - model.cashback;

    // The initial period True Cost includes the interest-only interest but not its capital, as on the monthly path.
    // A for-term product has no initial period True Cost.
    const trueCostInitial = isForTerm
      ? null
      : repaymentInitialRepayment * initialPeriod + interestOnlyInitialMonthlyPayment * initialPeriod
        + model.additionalFees - model.cashback;

    // The ERC for repaying at the end of the custom True Cost period, with calculateErcCharge's rules: only when the
    // product applies ERCs, the EarlyRepayment fee is a one-off cost and a custom period is set. It is charged on the
    // balance left after the period's payments, or on the opening balances (fees added to the loan included, rolled
    // up over any delayed start).
    const ercScenario = fees?.find(fee => fee.feeType === ProductFeeType.EarlyRepayment)?.feeCalculationScenario;
    const ercMonths = Math.min(Math.floor(customTrueCostPeriod ?? 0), term);
    let ercCharge = 0;
    if (product?.earlyRepaymentChargesErcApplied && ercScenario === FeeCalculationScenario.OneOffCost
      && (customTrueCostPeriod ?? 0) > 0 && ercMonths > 0) {
      const balanceAfterPeriod = Math.max(repaymentBalanceAfter(ercMonths) + interestOnlyBalance, 0);
      const originalLoanAmount = Math.max(repaymentBalance + interestOnlyBalance, 0);
      const chargeBasis = product.ercChargeBasis === ProductErcChargeBasis.OriginalLoanAmount
        ? originalLoanAmount
        : balanceAfterPeriod;

      ercCharge = chargeBasis * this.getErcRate(product, ercMonths, referenceDate);
    }

    // True Cost over the custom period, or over the initial period rounded to the nearest year (less any delayed
    // start) when no custom period is set: the payments over the period, plus one-off fees and the ERC, less cashback.
    // Months in the initial period are charged the initial payment, and months after it (from a custom period or from
    // rounding up to the nearest year) the revert payment. As in calculateCustomTrueCost, it includes the
    // interest-only interest but not its capital, and a for-term product with no custom period gets the full-term
    // True Cost.
    const nearestYearPeriod = Math.max(this.calculateNearestYearLoanPeriod(model.loanPeriods[0].term) ?? 0, 0);
    const trueCostPeriod = Math.min(
      customTrueCostPeriod ? Math.max(Math.floor(customTrueCostPeriod), 0) : Math.max(nearestYearPeriod - delayedStart, 0),
      term);
    const trueCostInitialMonths = Math.min(trueCostPeriod, initialPeriod);
    const trueCostRevertMonths = trueCostPeriod - trueCostInitialMonths;
    const trueCostCustom = isForTerm && !customTrueCostPeriod
      ? trueCost
      : repaymentInitialRepayment * trueCostInitialMonths
        + interestOnlyInitialMonthlyPayment * trueCostInitialMonths
        + monthlyPaymentRevert * trueCostRevertMonths
        + interestOnlyRevertMonthlyPayment * trueCostRevertMonths
        + model.additionalFees - model.cashback + ercCharge;

    // The balance left at the initial period rounded to the nearest year (less any delayed start), or 0 when those
    // months cover the whole term: the repayment part's balance after those payments plus the interest-only part.
    // Months after the initial period (from rounding up to the nearest year) are at the revert rate, as on the monthly
    // path and as for the ERC and the repayment illustration. As on the monthly path, the custom True Cost period isn't
    // used.
    const outstandingBalanceMonths = Math.max(nearestYearPeriod - delayedStart, 0);
    const outstandingBalance = outstandingBalanceMonths >= term
      ? 0
      : repaymentBalanceAfter(outstandingBalanceMonths) + interestOnlyBalance;

    // APRC. The adjusted model is the loan itself unless there is a delayed start. With one, the payments start after
    // the delay and the target is the rolled-up balances, as on the monthly path.
    const targetPvSum = adjustedModel.repaymentLoanAmount + adjustedModel.interestOnlyLoanAmount
      + adjustedModel.loanFees + adjustedModel.cashback - adjustedModel.additionalFees;
    const aprc = this.seekAprc(testAprc => {
      let sumOfPv = 0;
      for (let i = 0; i < term; i++) {
        const discFactor = Math.pow(1 / (1 + testAprc), (i + 1) / 12);
        let payment = i < initialPeriod
          ? repaymentInitialRepayment + interestOnlyInitialMonthlyPayment
          : monthlyPaymentRevert + interestOnlyRevertMonthlyPayment;

        // The interest-only capital is repaid with the last payment
        if (i === term - 1) {
          payment += interestOnlyBalance;
        }

        sumOfPv += payment * discFactor;
      }

      return sumOfPv;
    }, targetPvSum);

    // With a delayed start this leaves out the interest rolled up over the delay, as the monthly calculation does.
    const totalInterestPayable =
      trueCost -
      adjustedModel.repaymentLoanAmount -
      adjustedModel.interestOnlyLoanAmount -
      adjustedModel.loanFees -
      adjustedModel.additionalFees +
      adjustedModel.cashback;

    const initialPeriodCalculation = {
      repaymentInitialMonthlyRepayments: repaymentInitialRepayment,
      interestOnlyInitialMonthlyRepayments: interestOnlyInitialMonthlyPayment,
    } as LoanPeriodCalculation;
    const revertPeriodCalculation = {
      repaymentInitialMonthlyRepayments: monthlyPaymentRevert,
      interestOnlyInitialMonthlyRepayments: interestOnlyRevertMonthlyPayment,
    } as LoanPeriodCalculation;

    return {
      trueCost,
      trueCostInitial,
      trueCostCustom,
      initialMonthlyPayment: repaymentInitialRepayment + interestOnlyInitialMonthlyPayment,
      monthlyPaymentRevert: monthlyPaymentRevert + interestOnlyRevertMonthlyPayment,
      // As on the monthly path, this is also true when the initial period has no months left
      isForTerm: model.loanPeriods[0].term < 1,
      outstandingBalance,
      ercCharge,
      // One entry per loan period, so a for-term product has a single entry
      loanPeriodCalculations: isForTerm
        ? [initialPeriodCalculation]
        : [initialPeriodCalculation, revertPeriodCalculation],
      aprc,
      totalInterestPayable,
    };
  }

  /**
   * The annual calculation's default True Cost period: the initial period rounded to the nearest year, and at least
   * minimumDefaultTrueCostPeriod months when there is an initial period. Matches CalculateNearestYearLoanPeriod in the
   * backend's StandardCalcsService.
   */
  public calculateNearestYearLoanPeriod(initialPeriod: number | null): number | null {
    if (!initialPeriod) {
      return null;
    }
    //Use special rounding logic to calculate the nearest full year period
    const nearestYearPeriod = Math.floor((initialPeriod + 2) / 12) * 12;

    // Months after the initial period are charged the revert payment. A for-term product has no initial period, so it
    // gets no minimum.
    return initialPeriod >= 1 ? Math.max(nearestYearPeriod, this.minimumDefaultTrueCostPeriod) : nearestYearPeriod;
  }

  private calculateAPRCMonthly(loan: Loan) {
    // Copy the loan keeping its prototype, so a Loan keeps its loanType getter. A spread ({ ...loan }) drops it, and
    // the fees added to an interest-only loan were then treated as a repayment balance.
    const aprcLoanModel = Object.assign(Object.create(Object.getPrototypeOf(loan)), loan) as Loan;

    if (aprcLoanModel.loanPeriods.some(x => x.rateType === RateType.Fixed)) {
      const maxFixedRepaymentRate = Math.max(
        ...loan.loanPeriods.filter(x => x.rateType === RateType.Fixed).map(x => x.repaymentRate));
      const maxFixedInterestOnlyRate = Math.max(
        ...loan.loanPeriods.filter(x => x.rateType === RateType.Fixed).map(x => x.interestOnlyRate ?? 0));
      const revertRate = loan.loanPeriods.find(x => x.term === -1);

      if (revertRate && revertRate.repaymentRate < maxFixedRepaymentRate) {
        aprcLoanModel.loanPeriods = aprcLoanModel.loanPeriods.map(x =>
          x.term === -1 ? { ...x, repaymentRate: maxFixedRepaymentRate } : x);
      }

      if (revertRate && revertRate.repaymentRate < maxFixedInterestOnlyRate) {
        aprcLoanModel.loanPeriods = aprcLoanModel.loanPeriods.map(x =>
          x.term === -1 ? { ...x, interestOnlyRate: maxFixedInterestOnlyRate } : x);
      }
    }

    const loanPeriodCalculations = this.performLoanPeriodCalculations(aprcLoanModel);
    const monthlyPaymentCalculations = this.performMonthlyCalculations(aprcLoanModel, loanPeriodCalculations);
    const targetPvSum = loan.repaymentLoanAmount
      + loan.interestOnlyLoanAmount
      + loan.loanFees
      + loan.cashback
      - loan.additionalFees;

    return this.seekAprc(testAprc => monthlyPaymentCalculations
      .map((payment, monthIndex) => {
        const discFactor = Math.pow(1 / (1 + testAprc), (monthIndex + 1) / 12);
        return (payment.repaymentTotalRepayment + payment.interestOnlyCapitalRepaid +
          (payment.interestOnlyLoanOsAtBeginningOfMonth * payment.interestOnlyInterest)) * discFactor;
      }).reduce((accumulator, current) => {
        return accumulator + current;
      }, 0), targetPvSum);
  }

  public fastCalculate(
    loan: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): CalculationResult {
    switch (loan.capitalRestPeriod) {
      case LenderCapitalRestPeriod.Daily:
      case LenderCapitalRestPeriod.Monthly:
        return this.fastCalculateMonthly(loan, product, fees, customTrueCostPeriod, referenceDate);
      case LenderCapitalRestPeriod.Annual:
        return this.fastCalculateYearly(loan, product, fees, customTrueCostPeriod, referenceDate);
      default:
        throw new Error('Unsupported capital rest period.');
    }
  }

  private fastCalculateMonthly(
    loan: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): CalculationResult {
    const { loanAmount, loanAmountIo } = this.getLoanAmountsIncludingFees(loan);

    const { adjustedModel, rolledUpLoanAmount, rolledUpLoanAmountIo } =
      this.adjustLoanModel(loan, loanAmount, loanAmountIo);

    const shouldCalculateErc = !!product?.earlyRepaymentChargesErcApplied
      && fees?.some(fee => fee.feeType === ProductFeeType.EarlyRepayment
        && fee.feeCalculationScenario === FeeCalculationScenario.OneOffCost)
      && (customTrueCostPeriod ?? 0) > 0;

    // Calculate payments using adjusted model
    const monthlyPeriodPayments =
      this.calculateMonthlyPeriodPayments(adjustedModel, rolledUpLoanAmount, rolledUpLoanAmountIo);
    const monthlyPaymentCalculations = shouldCalculateErc
      ? this.performMonthlyCalculations(adjustedModel, this.performLoanPeriodCalculations(adjustedModel))
      : undefined;
    const lastPayment = monthlyPeriodPayments[monthlyPeriodPayments.length - 1];
    const monthlyPaymentRevert = lastPayment.monthlyPayments;
    const monthlyPaymentRevertIo = lastPayment.monthlyPaymentsIo;
    const oneOffPayments = loan.additionalFees - loan.cashback;

    // Recalculate trueCost using adjusted values
    const sumPayments = monthlyPeriodPayments.reduce((acc, val) =>
      acc + (val.monthlyPayments + val.monthlyPaymentsIo) * val.term, 0);
    const remainingTerm =
      Math.max(adjustedModel.term - monthlyPeriodPayments.reduce((acc, val) => acc + val.term, 0), 0);
    const trueCost = sumPayments + (monthlyPaymentRevert + monthlyPaymentRevertIo) * remainingTerm +
      oneOffPayments + rolledUpLoanAmountIo;

    const isForTerm = loan.loanPeriods[0].term < 1;
    // trueCostInitial now excludes the delay (no payments during first 3 months)
    const trueCostInitial = isForTerm ? null :
      (monthlyPeriodPayments[0].monthlyPayments + monthlyPeriodPayments[0].monthlyPaymentsIo) *
      adjustedModel.loanPeriods[0].term + oneOffPayments;

    // Update custom true cost using rolled up values and adjustedModel
    const trueCostCustomBase =
      this.calculateCustomTrueCost(rolledUpLoanAmount, rolledUpLoanAmountIo, trueCost, loan, customTrueCostPeriod);
    const ercCharge = this.calculateErcCharge(
      product,
      adjustedModel,
      rolledUpLoanAmount,
      rolledUpLoanAmountIo,
      fees,
      customTrueCostPeriod ?? 0,
      undefined,
      monthlyPaymentCalculations,
      referenceDate,
    );
    const trueCostCustom = trueCostCustomBase + ercCharge;

    return {
      trueCost: trueCost,
      trueCostInitial: trueCostInitial,
      trueCostCustom: trueCostCustom,
      initialMonthlyPayment: monthlyPeriodPayments[0].monthlyPayments +
        monthlyPeriodPayments[0].monthlyPaymentsIo,
      monthlyPaymentRevert: monthlyPaymentRevert + monthlyPaymentRevertIo,
      isForTerm,
      outstandingBalance: this.calculateOutstandingBalance(loan, rolledUpLoanAmount, rolledUpLoanAmountIo),
      ercCharge,
    };
  }

  private fastCalculateYearly(
    loan: Loan,
    product?: Product,
    fees?: CustomProductFees[] | null,
    customTrueCostPeriod?: number | null,
    referenceDate: Date = new Date(),
  ): CalculationResult {
    // Just piggyback off the regular calculate for this.
    return this.performLoanCalculationYearly(loan, product, fees, customTrueCostPeriod, referenceDate);
  }

  // New helper to compute rolled-up amount given a base amount, monthly rate and delayMonths
  private getRolledUpAmount(baseAmount: number, monthlyRate: number, delayMonths: number): number {
    return baseAmount * Math.pow(1 + monthlyRate, delayMonths);
  }

  /**
   * Opening balances at completion: fees added to the loan sit on the repayment part
   * (or the interest-only part for interest-only loans).
   */
  private getLoanAmountsIncludingFees(loan: Loan): { loanAmount: number; loanAmountIo: number } {
    const isInterestOnly = loan.loanType === LoanType.InterestOnly;
    return {
      loanAmount: loan.repaymentLoanAmount + (isInterestOnly ? 0 : loan.loanFees),
      loanAmountIo: loan.interestOnlyLoanAmount + (isInterestOnly ? loan.loanFees : 0),
    };
  }

  /**
   * Applies a delayed start. `loanAmount` and `loanAmountIo` must already include fees added to the loan
   * (see getLoanAmountsIncludingFees): those fees are part of the balance from completion, so they accrue
   * interest over the delay with the rest of the loan. The adjusted model therefore carries the fees inside
   * its balances and has `loanFees: 0`, so they are not added a second time by the period/monthly calculations.
   */
  public adjustLoanModel(loan: Loan, loanAmount: number, loanAmountIo: number)
    : { adjustedModel: Loan; rolledUpLoanAmount: number; rolledUpLoanAmountIo: number } {
    const delayMonths = loan.delayedStart;
    if (!delayMonths || delayMonths <= 0) {
      return { adjustedModel: loan, rolledUpLoanAmount: loanAmount, rolledUpLoanAmountIo: loanAmountIo };
    }
    const firstPeriod = loan.loanPeriods[0];
    // firstPeriod.term = firstPeriod.term - delayMonths;
    const monthlyRate = firstPeriod.repaymentRate / 12;
    const monthlyIoRate = (firstPeriod.interestOnlyRate ?? 0) / 12;
    const rolledUpLoanAmount = this.getRolledUpAmount(loanAmount, monthlyRate, delayMonths);
    const rolledUpLoanAmountIo = this.getRolledUpAmount(loanAmountIo, monthlyIoRate, delayMonths);
    const adjustedLoanPeriods = JSON.parse(JSON.stringify(loan.loanPeriods));
    adjustedLoanPeriods[0].term = firstPeriod.term - delayMonths;
    const adjustedModel: Loan = {
      ...loan,
      loanPeriods: adjustedLoanPeriods,
      term: loan.term - delayMonths,
      repaymentLoanAmount: rolledUpLoanAmount,
      interestOnlyLoanAmount: rolledUpLoanAmountIo,
      loanFees: 0,
      loanType: loan.loanType,
    };
    return { adjustedModel, rolledUpLoanAmount, rolledUpLoanAmountIo };
  }

  private calculateCustomTrueCost(
    loanAmount: number,
    loanAmountIo: number,
    trueCost: number,
    originalModel: Loan,
    customTrueCostPeriod?: number | null,
  ) {
    const fullYearModel = new Loan(
      originalModel.capitalRestPeriod,
      originalModel.repaymentLoanAmount, originalModel.interestOnlyLoanAmount, originalModel.loanFees,
      originalModel.additionalFees, originalModel.cashback, originalModel.term,
      customTrueCostPeriod ? originalModel.loanPeriods : this.getFullYearLoanPeriods(originalModel), originalModel.delayedStart);

    const { adjustedModel } = this.adjustLoanModel(fullYearModel, loanAmount, loanAmountIo);
    const monthlyPeriodPayments = this.calculateMonthlyPeriodPayments(adjustedModel, loanAmount, loanAmountIo);

    const oneOffPayments = adjustedModel.additionalFees - adjustedModel.cashback;

    const isForTerm = originalModel.loanPeriods[0].term < 1;

    const targetPeriod =
      customTrueCostPeriod ? customTrueCostPeriod : monthlyPeriodPayments.reduce((total, period) => total + period.term, 0);

    if ((isForTerm && !customTrueCostPeriod) || targetPeriod === null || targetPeriod === undefined) {
      return trueCost;
    }

    let remainingMonths = Math.max(Math.floor(targetPeriod), 0);
    const monthlyPayments = monthlyPeriodPayments.reduce((total, payment) => {
      const months = Math.min(remainingMonths, payment.term);
      remainingMonths -= months;
      return total + months * (payment.monthlyPayments + payment.monthlyPaymentsIo);
    }, 0);

    return monthlyPayments + oneOffPayments;
  }

  private calculateErcCharge(
    product: Product | undefined,
    loan: Loan,
    loanAmount: number,
    loanAmountIo: number,
    fees: CustomProductFees[] | null | undefined,
    period: number,
    loanPeriodCalculations?: LoanPeriodCalculation[],
    monthlyPayments?: MonthlyCalculation[],
    referenceDate: Date = new Date(),
  ): number {
    const ercScenario = fees?.find(fee => fee.feeType === ProductFeeType.EarlyRepayment)?.feeCalculationScenario;
    if (!product?.earlyRepaymentChargesErcApplied || ercScenario !== FeeCalculationScenario.OneOffCost || period <= 0) {
      return 0;
    }

    const availableMonthlyPayments = monthlyPayments ??
      (loanPeriodCalculations ? this.performMonthlyCalculations(loan, loanPeriodCalculations) :
        this.performMonthlyCalculations(loan, this.performLoanPeriodCalculations(loan)));
    const months = Math.min(Math.floor(period), availableMonthlyPayments.length);
    if (months <= 0) {
      return 0;
    }

    const lastPayment = availableMonthlyPayments[months - 1];
    const outstandingBalance = Math.max(
      lastPayment.repaymentLoanOsAtBeginningOfMonth - this.getRepaymentCapitalRepaid(lastPayment) +
      lastPayment.interestOnlyLoanOsAtBeginningOfMonth,
      0,
    );
    const originalLoanAmount = Math.max(loanAmount + loanAmountIo, 0);
    const chargeBasis = product.ercChargeBasis === ProductErcChargeBasis.OriginalLoanAmount
      ? originalLoanAmount
      : outstandingBalance;
    const chargeRate = this.getErcRate(product, months, referenceDate);

    return chargeBasis * chargeRate;
  }

  /**
   * The ERC rate for repaying after `months` payments. If every band has an end date, the first band still running on
   * the 1st of the month after the last payment is used; otherwise each band is a year. Undated bands that reach the
   * product's fixed end date are dated from it first, so they use the dated rule (MAV-702). Used by the monthly and
   * annual calculations. Matches GetErcRate in the backend's StandardCalcsService.
   */
  private getErcRate(product: Product, months: number, referenceDate: Date = new Date()): number {
    const ranks = this.anchorErcRanks(product.ercRanks ?? [], this.getFixedEndDate(product), referenceDate);
    if (!ranks.length) {
      return 0;
    }

    const dateBasedRanks = ranks.every(rank => !!rank.until);
    if (dateBasedRanks) {
      const exitDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + months + 1, 1);
      const matchingRank = ranks.find(rank => new Date(`${rank.until}T23:59:59`) >= exitDate);
      return matchingRank?.rate ? matchingRank.rate / 100 : 0;
    }

    const rankIndex = Math.floor(months / 12);
    return rankIndex >= 0 && rankIndex < ranks.length ? ranks[rankIndex].rate / 100 : 0;
  }

  /**
   * The end date of the product's fixed period: the until date of its last rate (by ordinal), or null when that rate
   * has no until date. Matches ResiBtlProduct.FixedEndDate on the backend.
   */
  private getFixedEndDate(product: Product): Date | null {
    const lastRate = product.rates?.toSorted((a, b) => b.ordinal - a.ordinal)[0];
    // Read the date as a local date, as the ERC bands' until dates are compared in getErcRate
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(lastRate?.until ?? '');
    return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
  }

  /**
   * Dates undated ERC bands from the product's fixed end date when the bands reach it, so that no band runs past the end
   * of the fixed period (MAV-702). Band i of n then ends on the fixed end date less (n - 1 - i) years, so the last band
   * ends with the fixed period. Otherwise the bands are returned as they are.
   *
   * The bands reach the fixed end date when n × 12 ≥ monthsLeft − 2. monthsLeft is the number of months from the 1st of
   * the month after `today` to the fixed end date, where an end date on the last day of a month counts as the 1st of
   * the next month, as Loan.getLoanPeriodsFromProduct works out the initial period. The 2 months' slack lets bands of
   * whole years reach an end date that is a month or two past a whole number of years away.
   *
   * Left as they are: schedules where every band is dated, products with no fixed end date, and schedules with fewer
   * bands than the time left (e.g. a 5-year fix with ERCs on the first 2 years only), whose bands run a year at a time
   * from today. A schedule with only some bands dated counts as undated, as it does in getErcRate. Bands that have
   * already ended are kept. Matches ResiBtlProductErcExtensions.AnchorToFixedEndDate on the backend.
   */
  private anchorErcRanks(ranks: ResiBtlProductErc[], fixedEndDate: Date | null, today: Date): ResiBtlProductErc[] {
    if (!fixedEndDate || !ranks.length || ranks.every(rank => !!rank.until)) {
      return ranks;
    }

    const dayAfterFixedEnd = new Date(fixedEndDate.getFullYear(), fixedEndDate.getMonth(), fixedEndDate.getDate() + 1);
    const periodEnd = dayAfterFixedEnd.getDate() === 1 ? dayAfterFixedEnd : fixedEndDate;
    const firstOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const monthsLeft = (periodEnd.getFullYear() - firstOfNextMonth.getFullYear()) * 12
      + periodEnd.getMonth() - firstOfNextMonth.getMonth();
    if (ranks.length * 12 < monthsLeft - 2) {
      return ranks;
    }

    return ranks.map((rank, index) => {
      const year = fixedEndDate.getFullYear() + index - (ranks.length - 1);
      const month = fixedEndDate.getMonth();
      // As DateOnly.AddYears on the backend: 29 February becomes 28 February in a year that isn't a leap year
      const day = Math.min(fixedEndDate.getDate(), new Date(year, month + 1, 0).getDate());
      return {
        rate: rank.rate,
        until: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      };
    });
  }

  public calculateMonthlyPeriodPayments(model: Loan, loanAmount: number, loanAmountIo: number):
  MonthlyPaymentCalculations[] {
    const monthlyPeriodPayments: MonthlyPaymentCalculations[] = [];
    let totalPrincipalRepaid = 0;
    let totalTermElapsed = 0;
    for (const period of model.loanPeriods) {
      if (totalTermElapsed >= model.term) {
        break;
      }
      const isRevert = period.term === -1;
      let term = period.term === -1 ?
        model.term - model.loanPeriods.filter(l => l.term !== -1).reduce((acc, val) => acc + val.term, 0) :
        period.term;

      term = Math.min(model.term - totalTermElapsed, term);

      const rate = period.repaymentRate / 12;
      const monthlyPayments = rate * (loanAmount - totalPrincipalRepaid)
            / (1 - Math.pow(1 + rate, -(model.term - totalTermElapsed)));
      const monthlyPaymentsIo = loanAmountIo * (period.interestOnlyRate ?? 0) / 12;
      const principalRepaid = (monthlyPayments - rate * (loanAmount - totalPrincipalRepaid)) *
            (Math.pow(1 + rate, term) - 1) / rate;
      const principalRepaidIo = monthlyPaymentsIo * term;

      totalPrincipalRepaid += principalRepaid;
      totalTermElapsed += term;
      monthlyPeriodPayments.push({
        monthlyPayments: monthlyPayments,
        principalRepaid: principalRepaid,
        monthlyPaymentsIo: monthlyPaymentsIo,
        principalRepaidIo: principalRepaidIo,
        term: term,
        isRevert: isRevert,
      });
    }

    return monthlyPeriodPayments;
  }

  public calculateOutstandingBalance(model: Loan, loanAmount: number, loanAmountIo: number): number {
    let totalPrincipalRepaid = 0;
    let totalTermElapsed = 0;
    let outstandingBalance = loanAmount + loanAmountIo;
    const calculatedLoanPeriods: LoanPeriod[] = this.getFullYearLoanPeriods(model);
    if (calculatedLoanPeriods.length > 0) {
      calculatedLoanPeriods[0].term = model.loanPeriods[0].term - (model.delayedStart ?? 0);
    }
    const totalCalculatedTerm = calculatedLoanPeriods.reduce((acc, val) => acc + val.term, 0);

    if (totalCalculatedTerm >= model.term) {
      return 0; //If the initial periods are longer than the loan period there will be no outstanding balance left
    }

    for (const period of calculatedLoanPeriods) {
      const rate = period.repaymentRate / 12;
      const monthlyPayments = rate * (loanAmount - totalPrincipalRepaid)
          / (1 - Math.pow(1 + rate, -((model.term - (model.delayedStart ?? 0)) - totalTermElapsed)));
      const principalRepaid = (monthlyPayments - rate * (loanAmount - totalPrincipalRepaid)) *
          (Math.pow(1 + rate, period.term) - 1) / rate;

      totalPrincipalRepaid += principalRepaid;
      totalTermElapsed += period.term;
      outstandingBalance -= principalRepaid;
    }

    return outstandingBalance;
  }

  /**
   * The initial periods, each rounded to the nearest year. Months added by rounding up are charged at the next period's
   * rate. With an initial period, the periods come to at least minimumDefaultTrueCostPeriod months: a single initial
   * period is rounded up to at least that, and a stepped product whose periods all round down to 0 uses its periods as
   * they are, then the revert rate for any months still short. Matches GetFullYearLoanPeriods in the backend's
   * StandardCalcsService.
   */
  private getFullYearLoanPeriods(model: Loan) {
    // A first period of under a month is treated as for-term (see calculateCustomTrueCost), so it gets no minimum.
    const hasInitialPeriod = model.loanPeriods.length > 0 && model.loanPeriods[0].term >= 1;
    const revertIndex = model.loanPeriods.findIndex(period => period.term === -1);
    const initialPeriodCount = revertIndex === -1 ? model.loanPeriods.length : revertIndex;

    let calculatedLoanPeriods: LoanPeriod[] = [];
    for (let i = 0; i < model.loanPeriods.length; i++) {
      const period = model.loanPeriods[i];
      if (period.term === -1) {
        break;
      }

      let loanPeriod = Math.floor((period.term + 2) / 12) * 12;

      // Under 10 months rounds down to 0, so round a single initial period up to the minimum instead. The months after
      // it are added below, at the next (revert) period's rate.
      if (hasInitialPeriod && initialPeriodCount === 1) {
        loanPeriod = Math.max(loanPeriod, this.minimumDefaultTrueCostPeriod);
      }

      if (loanPeriod > period.term) {
        calculatedLoanPeriods.push({
          repaymentRate: period.repaymentRate,
          term: period.term,
          interestOnlyRate: period.interestOnlyRate,
          rateType: period.rateType,
          until: period.until,
        } as LoanPeriod);

        calculatedLoanPeriods.push({
          repaymentRate: model.loanPeriods[i + 1].repaymentRate,
          term: loanPeriod - period.term,
          interestOnlyRate: model.loanPeriods[i + 1].interestOnlyRate,
          rateType: model.loanPeriods[i + 1].rateType,
        } as LoanPeriod);
      } else {
        calculatedLoanPeriods.push({
          repaymentRate: period.repaymentRate,
          term: loanPeriod,
          interestOnlyRate: period.interestOnlyRate,
          rateType: period.rateType,
          until: period.until,
        } as LoanPeriod);
      }
    }

    // Each stepped period rounds to whole years, so the total is short of the minimum only when every period rounds down
    // to 0. Then, as for a single initial period, use the periods as they are, and the revert rate for any months still
    // short of the minimum. The periods are copied, as calculateOutstandingBalance changes the first one's term.
    const fullYearTotal = calculatedLoanPeriods.reduce((total, period) => total + period.term, 0);
    const revertPeriod = model.loanPeriods.find(period => period.term === -1);
    if (hasInitialPeriod && initialPeriodCount > 1 && fullYearTotal < this.minimumDefaultTrueCostPeriod && revertPeriod) {
      calculatedLoanPeriods = model.loanPeriods.slice(0, initialPeriodCount).map(period => ({
        repaymentRate: period.repaymentRate,
        term: period.term,
        interestOnlyRate: period.interestOnlyRate,
        rateType: period.rateType,
        until: period.until,
      } as LoanPeriod));

      const initialPeriodsTotal = calculatedLoanPeriods.reduce((total, period) => total + period.term, 0);
      if (initialPeriodsTotal < this.minimumDefaultTrueCostPeriod) {
        calculatedLoanPeriods.push({
          repaymentRate: revertPeriod.repaymentRate,
          term: this.minimumDefaultTrueCostPeriod - initialPeriodsTotal,
          interestOnlyRate: revertPeriod.interestOnlyRate,
          rateType: revertPeriod.rateType,
        } as LoanPeriod);
      }
    }

    return calculatedLoanPeriods;
  }

  public performBridgingLoanCalculation(loan: Loan, model: BdgAffordabilityRequest
    | ProductsBridgingModelRequest, product: BridgingProduct): FullCalculationResult {
    const loanPeriodCalculations = this.performLoanPeriodCalculations(loan);
    const monthlyPaymentCalculations = this.performMonthlyCalculations(loan, loanPeriodCalculations);

    const targetPvSum =
      loan.interestOnlyLoanAmount
        + loan.loanFees
        + loan.cashback
        - loan.additionalFees;

    let aprcLowerBound = 0;
    let aprcUpperBound = 1;
    let aprc: number;

    let numIterations = 0;

    do {
      aprc = (aprcLowerBound + aprcUpperBound) / 2;
      const sumOfPv: number = monthlyPaymentCalculations
        .map((payment, monthIndex) => {
          const discFactor = Math.pow(1 / (1 + aprc), (monthIndex + 1) / 12);
          return (payment.repaymentTotalRepayment + payment.interestOnlyCapitalRepaid +
            (payment.interestOnlyLoanOsAtBeginningOfMonth * payment.interestOnlyInterest)) * discFactor;
        }).reduce((accumulator, current) => {
          return accumulator + current;
        }, 0);

      if (Math.abs(sumOfPv - targetPvSum) === 0) {
        break;
      }

      if (sumOfPv < targetPvSum) {
        aprcUpperBound = aprc;
      } else {
        aprcLowerBound = aprc;
      }
    } while (++numIterations < this.maxAprcIterations);

    const trueCostInitial: number | null = this.calculateTrueCostInitial(loan, loanPeriodCalculations, null);

    const totalInterestPayable: number = this.calculateTotalInterestPayable(monthlyPaymentCalculations);

    let trueCost = 0;
    const isForTerm = false;

    switch (model.methodOfRepayment) {
      case MethodOfRepayment.Serviced:
        trueCost = this.calculateTrueCost(monthlyPaymentCalculations, loan);
        if (model.loanAmount) {
          product.grossLoan = loan.loanFees + model.loanAmount;
        }
        return this.toResult({
          loanPeriodCalculations,
          trueCost,
          trueCostInitial,
          trueCostCustom: 0,
          aprc,
          totalInterestPayable,
          isForTerm,
          outstandingBalance: 0,
        });

      case MethodOfRepayment.RolledUp:
      case MethodOfRepayment.Retained:
        // Perform custom Rolled Up calculation
        const interestRate = product.rates[0].rate / 100;
        const termTime = model.loanTerm ?? 0; //Months

        let startingBalance = loan.interestOnlyLoanAmount + loan.loanFees;
        let interest = 0;

        for (let i = 0; i < termTime; i++) {
          const interestPayable = startingBalance * interestRate;
          startingBalance += interestPayable;
          interest += interestPayable;
        }

        trueCost = loan.interestOnlyLoanAmount + interest + loan.loanFees + loan.additionalFees;
        product.grossLoan = loan.interestOnlyLoanAmount + interest + loan.loanFees;
        return this.toResult({
          loanPeriodCalculations,
          trueCost,
          trueCostInitial,
          trueCostCustom: 0,
          aprc,
          totalInterestPayable,
          isForTerm,
          outstandingBalance: 0,
        });

      // case MethodOfRepayment.Retained:
      //   trueCost = this.calculateTrueCost(monthlyPaymentCalculations, loan);
      //   product.grossLoan = loan.interestOnlyLoanAmount + totalInterestPayable + loan.loanFees;
      //   return { loanPeriodCalculations, trueCost, trueCostInitial, aprc, totalInterestPayable };
    }

    return this.toResult({
      loanPeriodCalculations,
      trueCost,
      trueCostInitial,
      trueCostCustom: 0,
      aprc,
      totalInterestPayable,
      isForTerm,
      outstandingBalance: 0,
    });
  }

  public performCalculations(
    products: Product[],
    model: AffordabilityRequest | BtlAffordabilityRequest | BdgAffordabilityRequest
      | ProductsResidentialModelRequest | ProductsBtlModelRequest | ProductsBridgingModelRequest,
    includeCashback = true,
    fees: CustomProductFees [] | null = null,
    customFees: CustomFee[],
    assumedLegalFee: number | null,
    useFullCalculations: boolean = false,
    customTrueCostPeriod: number | null = null,
    referenceDate: Date = new Date(),
  ) {
    if (this.isModelApplicableForCalculation(model)) {
      products.forEach(product => {
        const lastPeriod = product.rates.toSorted((a, b) => b.ordinal - a.ordinal)[0];
        if ((lastPeriod.initialPeriod || lastPeriod.until) && !product.revertRate) {
          return;
        }
        try {
          const loan = Loan.fromProduct(
            product,
            model,
            includeCashback,
            fees,
            [...customFees, ...(product.brokerFees ?? []), ...(product.assumedLegalFees ?? [])],
            assumedLegalFee,
          );
          if (useFullCalculations) {
            const result = this.performLoanCalculation(loan, product, fees, customTrueCostPeriod, referenceDate);
            this.setFullCalculatedValues(product, result);
          } else {
            const result = this.fastCalculate(loan, product, fees, customTrueCostPeriod, referenceDate);
            this.setCalculatedValues(product, result);
          }
        } catch {
          // Can happen if a product has invalid data. Not logging to Sentry as we can get several errors per search.
        }
      });
    }
  }

  public performBridgingCalculations(
    products: BridgingProduct[],
    model: BdgAffordabilityRequest
      | ProductsBridgingModelRequest,
    fees: CustomBridgingProductFees[] | null = null,
  ): BridgingProduct[] {
    products.forEach(product => {
      try {
        const loan = Loan.fromBridgingProduct(product, model, fees);
        const result = this.performBridgingLoanCalculation(loan, model, product);
        this.setBridgingCalculatedValues(model, product, result);
      } catch {
        // Can happen if a product has invalid data. Not logging to Sentry as we can get several errors per search.
      }
    });
    return [];
  }

  private setFullCalculatedValues(product: Product, calculationResult: FullCalculationResult) {
    const {
      loanPeriodCalculations, trueCost, trueCostInitial, aprc, totalInterestPayable,
    } = calculationResult;
    const displayTrueCost = +trueCost.toFixed(2);
    product.initialMonthlyPayment =
      loanPeriodCalculations[0].repaymentInitialMonthlyRepayments
      + loanPeriodCalculations[0].interestOnlyInitialMonthlyRepayments;
    product.monthlyPaymentRevert =
      loanPeriodCalculations[loanPeriodCalculations.length - 1].repaymentInitialMonthlyRepayments
      + loanPeriodCalculations[loanPeriodCalculations.length - 1].interestOnlyInitialMonthlyRepayments;
    product.initialMonthlyPaymentInterestOnly = loanPeriodCalculations[0].interestOnlyInitialMonthlyRepayments;
    product.monthlyPaymentRevertInterestOnly =
      loanPeriodCalculations[loanPeriodCalculations.length - 1].interestOnlyInitialMonthlyRepayments;
    product.trueCost = displayTrueCost;
    product.trueCostInitialPeriod = calculationResult.isForTerm ?
      null : Math.max(trueCostInitial ?? 0, 0);
    product.trueCostCustomPeriod = Math.max(calculationResult.trueCostCustom ?? 0, 0);
    product.trueCostErc = calculationResult.ercCharge ?? 0;
    product.aprc = +(aprc * 100).toFixed(1);
    product.totalInterestPayable = +totalInterestPayable.toFixed(2);
  }

  private setCalculatedValues(product: Product, calculationResult: CalculationResult) {
    const {
      trueCost, trueCostInitial, trueCostCustom, initialMonthlyPayment, monthlyPaymentRevert } = calculationResult;
    product.initialMonthlyPayment = initialMonthlyPayment;
    product.monthlyPaymentRevert = monthlyPaymentRevert;
    product.trueCost = +trueCost.toFixed(2);
    product.trueCostInitialPeriod =
      calculationResult.isForTerm ? null : Math.max(trueCostInitial ?? 0, 0);
    product.trueCostCustomPeriod = Math.max(trueCostCustom ?? 0, 0);
    product.trueCostErc = calculationResult.ercCharge ?? 0;
    product.outstandingBalance = calculationResult.outstandingBalance;
  }

  private setBridgingCalculatedValues(model: BdgAffordabilityRequest
    | ProductsBridgingModelRequest, product: BridgingProduct, calculationResult: FullCalculationResult) {
    const { loanPeriodCalculations, trueCost, trueCostInitial, aprc, totalInterestPayable } = calculationResult;
    // Retained and Rolled-up products should have a monthly rate of null
    if (model.methodOfRepayment === MethodOfRepayment.Retained
      || model.methodOfRepayment === MethodOfRepayment.RolledUp) {
      product.monthlyRate = null;
    } else {
      const { repaymentInitialMonthlyRepayments, interestOnlyInitialMonthlyRepayments } = loanPeriodCalculations[0];
      product.monthlyRate = repaymentInitialMonthlyRepayments + interestOnlyInitialMonthlyRepayments;
    }

    product.initialMonthlyPaymentInterestOnly = loanPeriodCalculations[0].interestOnlyInitialMonthlyRepayments;
    product.monthlyPaymentRevertInterestOnly =
      loanPeriodCalculations[loanPeriodCalculations.length - 1].interestOnlyInitialMonthlyRepayments;
    product.trueCost = +trueCost.toFixed(2);
    product.trueCostInitialPeriod = trueCostInitial;
    product.aprc = +(aprc * 100).toFixed(1);
    product.totalInterestPayable = +totalInterestPayable.toFixed(2);
  }

  private calculateTotalInterestPayable(monthlyPaymentCalculations: MonthlyCalculation[]) {
    return monthlyPaymentCalculations
      .map((calc) => this.getRepaymentInterestPaid(calc)
        + (calc.interestOnlyLoanOsAtBeginningOfMonth * calc.interestOnlyInterest))
      .reduce((accumulator, calc) => {
        return accumulator + calc;
      }, 0);
  }

  private calculateTrueCostInitial(
    loan: Loan,
    noFeeLoanPeriodCalculations: LoanPeriodCalculation[],
    customTrueCostPeriod: number | null,
  ) {
    return loan.loanPeriods.length > 1 && loan.loanPeriods[0].term > 0
      ? +(((noFeeLoanPeriodCalculations[0].repaymentInitialMonthlyRepayments +
        noFeeLoanPeriodCalculations[0].interestOnlyInitialMonthlyRepayments)
        * (customTrueCostPeriod ?? loan.loanPeriods[0].term)) + loan.additionalFees - loan.cashback).toFixed(2)
      : null;
  }

  private calculateTrueCost(monthlyPaymentCalculations: MonthlyCalculation[], loan: Loan): number {
    return (
      monthlyPaymentCalculations
        .map(calc => {
          return (
            this.getRepaymentInterestPaid(calc) +
            this.getRepaymentCapitalRepaid(calc) +
            calc.interestOnlyCapitalRepaid +
            calc.interestOnlyLoanOsAtBeginningOfMonth * calc.interestOnlyInterest
          );
        })
        .reduce((accumulator, calc) => {
          return accumulator + calc;
        }, 0) +
      loan.additionalFees -
      loan.cashback
    );
  }

  private isModelApplicableForCalculation(model: AffordabilityRequest | BtlAffordabilityRequest
    | BdgAffordabilityRequest | ProductsResidentialModelRequest | ProductsBtlModelRequest
    | ProductsBridgingModelRequest): boolean | undefined | 0 {
    return (model.loanAmount && model.repaymentMethod && model.mortgageTermYears && model.mortgageTermYears) &&
        !(model.repaymentMethod === RepaymentMethod.InterestOnlyPartAndPart && !model.interestOnlyAmount);
  }

  private performLoanPeriodCalculations(loan: Loan): LoanPeriodCalculation[] {
    const multiplier = 12;

    const calcs: LoanPeriodCalculation[] = [];
    let previousPeriodCalc: LoanPeriodCalculation | null = null;

    const interestOnlyInitialAmount =
      loan.interestOnlyLoanAmount + (loan.loanType === LoanType.InterestOnly ? loan.loanFees : 0);
    const termYears = loan.term / 12;
    let annuity = 0;

    for (const loanPeriod of loan.loanPeriods) {
      const remainingMonthsInTerm = loan.term - annuity;
      if (remainingMonthsInTerm <= 0) {
        break;
      }

      const prevAnnuity = annuity;
      annuity += Math.max(Math.min(loanPeriod.term, remainingMonthsInTerm), 0);
      const repaymentEarCompoundedMonthly = Math.pow(1 + (loanPeriod.repaymentRate / multiplier), multiplier) - 1;

      const repaymentLoanFeesAdjustment = loan.loanType === LoanType.InterestOnly
        ? 0
        : loan.loanFees;
      const repaymentCapitalOsAtBeginningOfPeriod = !previousPeriodCalc
        ? loan.repaymentLoanAmount + repaymentLoanFeesAdjustment
        : previousPeriodCalc.repaymentInitialMonthlyRepayments * previousPeriodCalc.repaymentCapitalOsAnnuityMonthly * 12;

      const repaymentPaymentAnnuityMonthly = (1 - Math.pow(1 / (1 + repaymentEarCompoundedMonthly),
        termYears - (!previousPeriodCalc ? 0 : (prevAnnuity / 12)))) / loanPeriod.repaymentRate;

      const interestOnlyEarCompoundedMonthly =
        loanPeriod.interestOnlyRate
          ? Math.pow(1 + (loanPeriod.interestOnlyRate / multiplier), multiplier) - 1
          : 0;

      const calculation: LoanPeriodCalculation = {
        repaymentCapitalOsAtBeginningOfPeriod: repaymentCapitalOsAtBeginningOfPeriod,
        repaymentCapitalOsAnnuityMonthly:
          (1 - Math.pow(1 / (1 + repaymentEarCompoundedMonthly), termYears - (annuity / 12))) /
          loanPeriod.repaymentRate,
        repaymentEarCompoundedMonthly: repaymentEarCompoundedMonthly,
        repaymentPaymentAnnuityMonthly: repaymentPaymentAnnuityMonthly,
        repaymentInitialMonthlyRepayments:
         repaymentCapitalOsAtBeginningOfPeriod / (repaymentPaymentAnnuityMonthly * 12),
        interestOnlyEarCompoundedMonthly: interestOnlyEarCompoundedMonthly,
        interestOnlyInitialMonthlyRepayments:
         interestOnlyInitialAmount * (Math.pow(1 + interestOnlyEarCompoundedMonthly, 1 / 12) - 1),
      };
      calcs.push(calculation);
      previousPeriodCalc = calculation;
    }
    return calcs;
  }

  public performMonthlyCalculations(loan: Loan, loanPeriodCalcs: LoanPeriodCalculation[]): MonthlyCalculation[] {
    const monthlyPayments: MonthlyCalculation[] = [];
    let currentPeriodIndex = 0;
    let monthsRemainingInCurrentLoanPeriod = loan.loanPeriods[currentPeriodIndex].term;

    for (let i = 0; i < loan.term; i++) {
      while (monthsRemainingInCurrentLoanPeriod === 0) {
        currentPeriodIndex++;
        if (!loan.loanPeriods[currentPeriodIndex]) {
          return monthlyPayments;
        }
        monthsRemainingInCurrentLoanPeriod = loan.loanPeriods[currentPeriodIndex].term ?? 0;
      }
      const interestOnlyLoanOsAtBeginningOfMonth = loan.interestOnlyLoanAmount +
        (loan.loanType === LoanType.InterestOnly ? loan.loanFees : 0);

      const repaymentLoanFeesAdjustment = loan.loanType === LoanType.InterestOnly
        ? 0
        : loan.loanFees;
      const payment: MonthlyCalculation = {
        loanPeriodIndex: currentPeriodIndex,
        repaymentLoanOsAtBeginningOfMonth: i === 0
          ? loan.repaymentLoanAmount + repaymentLoanFeesAdjustment
          : monthlyPayments[i - 1].repaymentLoanOsAtBeginningOfMonth - this.getRepaymentCapitalRepaid(monthlyPayments[i - 1]),
        repaymentMonthlyEffInterest:
          Math.pow(1 + loanPeriodCalcs[currentPeriodIndex]?.repaymentEarCompoundedMonthly, 1 / 12) - 1,
        repaymentTotalRepayment: loanPeriodCalcs[currentPeriodIndex]?.repaymentInitialMonthlyRepayments ?? 0,
        interestOnlyLoanOsAtBeginningOfMonth: interestOnlyLoanOsAtBeginningOfMonth,
        interestOnlyInterest:
          Math.pow(1 + loanPeriodCalcs[currentPeriodIndex]?.interestOnlyEarCompoundedMonthly, 1 / 12) - 1,
        interestOnlyCapitalRepaid: i === loan.term - 1 ? interestOnlyLoanOsAtBeginningOfMonth : 0,
      };
      monthlyPayments.push(payment);
      monthsRemainingInCurrentLoanPeriod--;
    }
    return monthlyPayments;
  }

  private getRepaymentCapitalRepaid(monthlyCalculation: MonthlyCalculation): number {
    return monthlyCalculation.repaymentTotalRepayment - this.getRepaymentInterestPaid(monthlyCalculation);
  }

  private getRepaymentInterestPaid(monthlyCalculation: MonthlyCalculation): number {
    return monthlyCalculation.repaymentLoanOsAtBeginningOfMonth * monthlyCalculation.repaymentMonthlyEffInterest;
  }

  private toResult(
    values: Omit<FullCalculationResult, 'initialMonthlyPayment' | 'monthlyPaymentRevert'>,
  ): FullCalculationResult {
    return {
      ...values,
      get initialMonthlyPayment() {
        return this.loanPeriodCalculations[0].interestOnlyInitialMonthlyRepayments
          + this.loanPeriodCalculations[0].repaymentInitialMonthlyRepayments;
      },
      get monthlyPaymentRevert() {
        return this.loanPeriodCalculations[this.loanPeriodCalculations.length - 1].interestOnlyInitialMonthlyRepayments
          + this.loanPeriodCalculations[this.loanPeriodCalculations.length - 1].repaymentInitialMonthlyRepayments;
      },
    };
  }

  private seekAprc(calculateSumOfPv: (testAprc: number) => number, targetPvSum: number) {
    let aprcLowerBound = 0;
    let aprcUpperBound = 1;
    let aprc: number;

    let numIterations = 0;

    do {
      aprc = (aprcLowerBound + aprcUpperBound) / 2;
      const sumOfPv = calculateSumOfPv(aprc);

      if (Math.abs(sumOfPv - targetPvSum) === 0) {
        break;
      }

      if (sumOfPv < targetPvSum) {
        aprcUpperBound = aprc;
      } else {
        aprcLowerBound = aprc;
      }
    } while (++numIterations < this.maxAprcIterations);
    return aprc;
  }
}
